/**
 * POST body for createStorefrontAuthorizeCheckout.
 * Success URL targets Accept Hosted return routes (no {PENDING_APPOINTMENT_ID} placeholder;
 * server appends pending id).
 */
export function buildStorefrontAuthorizeCheckoutRequest(params) {
  const {
    companyId,
    companyUrl,
    serviceSlug,
    service,
    selectedPackage,
    selectedAddons,
    appointmentDateTime,
    durationMinutes,
    chosenEmployee,
    clientId,
    clientName,
    clientEmail,
    clientPhone,
    totalPrice,
    depositAmount = 0,
    checkoutDeposit = false,
    selectedLocation = null,
  } = params;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const cancelPath = serviceSlug
    ? `/book/${encodeURIComponent(companyUrl)}/${encodeURIComponent(serviceSlug)}?book=1`
    : `/book/${encodeURIComponent(companyUrl)}`;
  const cancelUrl = `${origin}${cancelPath}`;

  const addons = (selectedAddons || []).map((addon) => ({
    id: addon.id || '',
    name: addon.name || '',
    price: parseFloat(addon.price || 0),
    duration: parseInt(addon.duration || 0, 10),
    description: addon.description || '',
    taxAmount: 0,
    taxRate: 0,
  }));

  const pkg = selectedPackage
    ? {
        id: selectedPackage.id || '',
        name: selectedPackage.name || '',
        price: parseFloat(selectedPackage.price || 0),
        description: selectedPackage.description || '',
        duration: parseInt(selectedPackage.duration || 0, 10),
        bufferTime: parseInt(selectedPackage.bufferTime || 0, 10),
        sessions: parseInt(selectedPackage.sessions || 1, 10),
        validity: selectedPackage.validity ?? null,
        terms: selectedPackage.terms || '',
        includedServices: selectedPackage.includedServices || [],
      }
    : null;

  const employeeTitle =
    chosenEmployee.title || (chosenEmployee.isOwner ? 'Owner' : 'Staff Member');
  const employeePayload = {
    id: chosenEmployee.id,
    name: chosenEmployee.name,
    title: employeeTitle,
    isOwner: chosenEmployee.isOwner || false,
  };

  const servicePrice = parseFloat(service.price || 0);
  const fullTotal = Number(totalPrice) || 0;
  const rawDeposit = Math.min(Number(depositAmount) || 0, fullTotal);
  const useDeposit = Boolean(checkoutDeposit && rawDeposit > 0.01);
  const depositDue = useDeposit ? rawDeposit : 0;
  const chargeNow = useDeposit ? depositDue : fullTotal;
  const remainingBalance = useDeposit ? Math.max(0, fullTotal - depositDue) : 0;

  const appointmentData = {
    clientId,
    clientEmail,
    clientName,
    clientPhone: clientPhone || '',
    dateTime: appointmentDateTime.toISOString(),
    duration: durationMinutes,
    serviceId: service.id,
    serviceName: service.name,
    servicePrice,
    employeeId: chosenEmployee.id,
    employeeName: chosenEmployee.name,
    employeeTitle,
    employeeIsOwner: chosenEmployee.isOwner || false,
    staffId: chosenEmployee.id,
    employee: employeePayload,
    price: Number(chargeNow),
    totalPrice: fullTotal,
    remainingBalance,
    notes: '',
    timezone:
      typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
    selectedAddons: pkg ? [] : addons,
    selectedPackage: pkg,
    addons: pkg ? [] : addons,
    package: pkg,
    deposit: {
      amount: depositDue,
      enabled: useDeposit,
      tax: 0,
      total: depositDue,
      status: useDeposit ? 'pending' : 'not_required',
    },
    depositStatus: useDeposit ? 'pending' : 'not_required',
    paymentStatus: 'pending',
    isBalancePayment: false,
    bookingType: 'storefront',
    source: 'storefront',
    ...(selectedLocation?.id ? {
      locationId: selectedLocation.id,
      locationName: selectedLocation.name || '',
      locationAddress: selectedLocation.address || null,
    } : {}),
  };

  const paymentType = useDeposit ? 'deposit' : 'full';
  const successKind = paymentType === 'deposit' ? 'deposit' : 'payment';
  const successUrl = `${origin}/book/${encodeURIComponent(params.companyUrl)}/booking/success/${successKind}/authorize`;

  return {
    appointmentData,
    companyId,
    serviceDetails: {
      name: service.name,
      description: useDeposit
        ? `Deposit for ${service.name}`
        : `Booking: ${service.name}`,
      price: fullTotal,
    },
    successUrl,
    cancelUrl,
    paymentType,
  };
}
